import React, { useContext, useState } from 'react';
import { Colors } from '../../utils/theme';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import excel from '../../assets/images/actions/excel_icon.svg';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  ICompanyType,
  ICountry,
  IInterest,
  IMembershipType,
  IRecipientList,
  IRecipientListsQuery,
  ITitleInternal,
  RecipientListsDocument,
  useDeleteRecipientListMutation,
  useRecipientListsQuery,
  useRecipientsLazyQuery
} from '../../generated/graphql';
import moment from 'moment-timezone';
import { Box, Button } from '@mui/material';
import { People, Settings } from '@mui/icons-material';
import OverviewTable from '../Common/Tables/OverviewTable';
import Loader from '../Common/Loader';
import { exportDataAsExcel } from '../../utils/fileUtiles';
import { RecipientListType } from '../../models/recipientList';
import RecipientListStepper from './RecipientListWizard/RecipientListStepper';
import RecipientsTabs from './RecipientsTabs';

type InputType = ITitleInternal | ICompanyType | IMembershipType | IInterest | ICountry;

const RecipientList: React.FunctionComponent = () => {
  const { dispatch } = useContext(AppContext);
  const [openWizard, setOpenWizard] = useState(false);
  const [opentRecipientsTabs, setOpentRecipientsTabs] = useState(false);
  const { data, loading } = useRecipientListsQuery();
  const [deleteRecipientList] = useDeleteRecipientListMutation();
  const [getRecipients, getRecipientsResult] = useRecipientsLazyQuery({ fetchPolicy: 'no-cache' });

  const handleRemove = (rowData: IRecipientList) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IRecipientList) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteRecipientList({
      variables: {
        id: parseInt(rowData.id)
      },
      update(cache) {
        const recipientListQuery = cache.readQuery({
          query: RecipientListsDocument
        }) as IRecipientListsQuery;
        if (recipientListQuery.result) {
          const cacheRecipientListIndex = recipientListQuery.result.findIndex(
            (r) => r?.id === rowData.id
          );
          if (cacheRecipientListIndex > -1) {
            const data = recipientListQuery.result.filter((r) => r?.id !== rowData.id);
            cache.writeQuery({
              query: RecipientListsDocument,
              data: { result: data }
            });
          }
        }
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Recipient list deleted ${rowData.name}`, severity: 'success' }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const handleAddNewPressed = () => {
    dispatch({
      type: Actions.SetRecipientList,
      payload: {
        recipientList: {
          id: undefined,
          name: undefined,
          contactTitleIds: [],
          companyTypeIds: [],
          membershipTypeIds: [],
          interestIds: [],
          countryIds: [],
          excludedContactIds: [],
          includedContactIds: []
        } as RecipientListType
      }
    });
    setOpenWizard(true);
  };

  const handleSettingsPressed = (rowData: IRecipientList) => {
    setSelectedRecipientList(rowData);
    setOpenWizard(true);
  };

  const setSelectedRecipientList = (rowData: IRecipientList) => {
    dispatch({
      type: Actions.SetRecipientList,
      payload: {
        recipientList: {
          id: rowData.id,
          name: rowData.name,
          contactTitleIds: rowData.contact_titles.map((ct) => ct?.id),
          companyTypeIds: rowData.company_types.map((ct) => ct?.id),
          membershipTypeIds: rowData.membership_types.map((mt) => mt?.id),
          interestIds: rowData.interests.map((i) => i?.id),
          countryIds: rowData.countries.map((c) => c?.id),
          excludedContactIds: rowData.excluded_contact_ids
            ? JSON.parse(rowData.excluded_contact_ids)
            : [],
          includedContactIds: rowData.included_contact_ids
            ? JSON.parse(rowData.included_contact_ids)
            : []
        } as RecipientListType
      }
    });
  };

  const handleRecipientsPressed = (rowData: IRecipientList) => {
    setSelectedRecipientList(rowData);
    setOpentRecipientsTabs(true);
  };

  const renderRecipientsColumn = (rowData: IRecipientList) => {
    return (
      <Button id={rowData.id} onClick={() => handleRecipientsPressed(rowData)}>
        <People style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const renderSettingsColumn = (rowData: IRecipientList) => {
    return (
      <Button id={rowData.id} onClick={() => handleSettingsPressed(rowData)}>
        <Settings style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const renderExcelColumn = (rowData: IRecipientList) => {
    return (
      <Button id={rowData.id} onClick={() => exportRecipientListContacts(rowData)}>
        <img src={excel} alt="excelButton" />
      </Button>
    );
  };

  const exportRecipientListContacts = async (rowData: IRecipientList) => {
    try {
      // Use the same query as the Recipients tab so export matches what the user sees
      const { data, error } = await getRecipients({
        variables: { id: parseInt(rowData.id) }
      });
      if (error) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: error.message || 'Failed to download recipient list',
            autoHide: 6000
          }
        });
        return;
      }
      const recipients = data?.result?.filter((r) => r != null) ?? [];
      if (recipients.length === 0) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: 'No recipients found to export',
            autoHide: 6000
          }
        });
        return;
      }
      const listName = (rowData.name || 'recipient_list').replaceAll(' ', '_');
      const fileName = `${moment(new Date()).format('YYYY_MM_DD')}_${listName}`;
      const csvData = recipients.map((r) => ({
        'First Name': r?.first_name || '',
        'Last Name': r?.last_name || '',
        Title: r?.titleInternal?.name || '',
        Email: r?.email || ''
      }));
      exportDataAsExcel(csvData, fileName);
    } catch (error: any) {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error?.message || 'Failed to download recipient list',
          autoHide: 6000
        }
      });
    }
  };

  const formatColumnData = (input: InputType[]) => {
    const names = input.map((i: InputType) => i.name);
    return names.join(', ');
  };

  const handleCloseWizard = () => {
    setOpenWizard(false);
    dispatch({
      type: Actions.ResetRecipientList
    });
  };

  const handleCloseRecipientsTabs = () => {
    dispatch({
      type: Actions.ResetRecipientList
    });
    setOpentRecipientsTabs(false);
  };

  const columns = [
    {
      title: 'Recipients',
      width: 85,
      sorting: false,
      render: renderRecipientsColumn
    },
    {
      title: 'Configure',
      width: 80,
      sorting: false,
      render: renderSettingsColumn
    },
    {
      title: 'Name',
      field: 'name',
      width: 180.5
    },
    {
      title: 'Contact title',
      field: 'contact_titles',
      width: 368,
      render: (rowData: IRecipientList) =>
        formatColumnData(rowData.contact_titles as ITitleInternal[])
    },
    {
      title: 'Company type',
      field: 'company_types',
      render: (rowData: IRecipientList) =>
        formatColumnData(rowData.company_types as ICompanyType[]),
      width: 234
    },
    {
      title: 'Membership type',
      field: 'membership_types',
      render: (rowData: IRecipientList) =>
        formatColumnData(rowData.membership_types as IMembershipType[]),
      width: 210
    },
    {
      title: 'Country',
      field: 'countries',
      render: (rowData: IRecipientList) => formatColumnData(rowData.countries as ICountry[]),
      width: 150
    },
    {
      title: 'Interest',
      field: 'interest',
      render: (rowData: IRecipientList) => formatColumnData(rowData.interests as IInterest[]),
      width: 273
    },
    {
      title: 'Excel',
      width: 69,
      sorting: false,
      render: renderExcelColumn
    },
    {
      title: 'Remove',
      width: 71.5,
      sorting: false
    }
  ];

  return (
    <Box className="overviewPage">
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns}
          data={data?.result || []}
          loading={loading}
          handleDetails={() => true}
          handleRemove={handleRemove}
          pageSize={15}
          createButtonProps={{
            id: 'create-recipient-list-button',
            onClick: () => handleAddNewPressed(),
            text: 'Add List',
            disabled: false
          }}
        />
        {openWizard && <RecipientListStepper onClose={handleCloseWizard} />}
        {opentRecipientsTabs && <RecipientsTabs onClose={handleCloseRecipientsTabs} />}
        <Loader loading={getRecipientsResult.loading} />
      </Box>
    </Box>
  );
};

export default RecipientList;
